/**
 * Marker for how one candidate field compared with the Engage patient's:
 * Exact, Close, Different or Missing. Icon plus text, so the result never
 * depends on colour alone.
 */
import type { IconName } from '@fortawesome/fontawesome-svg-core';
import { Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import type { EhrMatchFieldResult } from '@models/ehr-sync';

import type { EhrSyncTone } from './tone';
import { toneTextColor } from './tone';

const messages = defineMessages<EhrMatchFieldResult>({
  exact: { id: 'ehrSync.evidence.exact', defaultMessage: 'Exact' },
  close: { id: 'ehrSync.evidence.close', defaultMessage: 'Close' },
  different: { id: 'ehrSync.evidence.different', defaultMessage: 'Different' },
  missing: { id: 'ehrSync.evidence.missing', defaultMessage: 'Missing' },
});

const RESULT_STYLE: Record<
  EhrMatchFieldResult,
  { icon: IconName; tone: EhrSyncTone }
> = {
  exact: { icon: 'check', tone: 'success' },
  close: { icon: 'tilde', tone: 'warning' },
  different: { icon: 'xmark', tone: 'error' },
  missing: { icon: 'minus', tone: 'grey' },
};

/** Props for {@link EvidenceMarker}. */
interface EvidenceMarkerProps {
  result: EhrMatchFieldResult;
}

/**
 * Icon and label for one field result.
 *
 * @param props - see {@link EvidenceMarkerProps}
 */
export default function EvidenceMarker({ result }: EvidenceMarkerProps) {
  const { formatMessage } = useIntl();
  const { icon, tone } = RESULT_STYLE[result];

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      data-result={result}
      sx={(theme) => ({ color: toneTextColor(theme, tone) })}
    >
      <Icon name={icon} type="fas" size="xs" color="inherit" />
      <Typography
        variant="caption"
        fontWeight="bold"
        sx={{ letterSpacing: '0.04em', textTransform: 'uppercase' }}
      >
        {formatMessage(messages[result])}
      </Typography>
    </Stack>
  );
}
